import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import timeEntryService from '@api/v1/timeEntryService'
import LoadingSpinner from '@components/common/LoadingSpinner'
import useDocumentTitle from '@hooks/useDocumentTitle'

dayjs.extend(isoWeek)

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function getWeekRange(baseDate) {
  const start = dayjs(baseDate).startOf('isoWeek')
  const end = start.add(6, 'day')
  return { start, end }
}

function formatWeekLabel(start, end) {
  if (start.month() === end.month()) {
    return `${start.format('MMMM D')} – ${end.format('D, YYYY')}`
  }
  return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`
}

const TimeSheet = () => {
  useDocumentTitle('Time Sheet')
  const queryClient = useQueryClient()
  const [weekOf, setWeekOf] = useState(() => dayjs().startOf('isoWeek').format('YYYY-MM-DD'))
  const { start, end } = useMemo(() => getWeekRange(weekOf), [weekOf])

  const weekDates = useMemo(
    () => DAYS.map((_, i) => start.add(i, 'day')),
    [start]
  )

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['timeSheetEntries', weekOf],
    queryFn: () =>
      timeEntryService.getEntriesForRange(
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD')
      ),
  })

  const { data: activeTimer } = useQuery({
    queryKey: ['activeTimer'],
    queryFn: () => timeEntryService.getActiveTimer(),
    refetchInterval: 30000,
  })

  const stopTimerMutation = useMutation({
    mutationFn: () => timeEntryService.stopTimer(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeTimer'] })
      queryClient.invalidateQueries({ queryKey: ['timeSheetEntries'] })
    },
  })

  // Group entries by date, then by matter
  const byDate = useMemo(() => {
    const map = {}
    for (const d of weekDates) {
      map[d.format('YYYY-MM-DD')] = []
    }
    for (const entry of entries) {
      const dateKey = entry.date?.split('T')[0]
      if (dateKey && map[dateKey]) {
        map[dateKey].push(entry)
      }
    }
    return map
  }, [entries, weekDates])

  // Unique matters across the week for row-based view
  const matterRows = useMemo(() => {
    const matterMap = new Map()
    for (const entry of entries) {
      const key = entry.matterName || entry.name || 'Unassigned'
      if (!matterMap.has(key)) {
        matterMap.set(key, { name: key, entries: {} })
      }
      const dateKey = entry.date?.split('T')[0]
      if (dateKey) {
        if (!matterMap.get(key).entries[dateKey]) {
          matterMap.get(key).entries[dateKey] = []
        }
        matterMap.get(key).entries[dateKey].push(entry)
      }
    }
    return [...matterMap.values()]
  }, [entries])

  // Daily and weekly totals
  const dailyTotals = useMemo(
    () => weekDates.map((d) => {
      const dateKey = d.format('YYYY-MM-DD')
      return (byDate[dateKey] || []).reduce((sum, e) => sum + e.hours, 0)
    }),
    [byDate, weekDates]
  )
  const weekTotal = dailyTotals.reduce((s, d) => s + d, 0)
  const billableTotal = entries.filter((e) => !e.isNonBillable).reduce((s, e) => s + e.hours, 0)

  const prevWeek = () => setWeekOf(start.subtract(7, 'day').format('YYYY-MM-DD'))
  const nextWeek = () => setWeekOf(start.add(7, 'day').format('YYYY-MM-DD'))
  const thisWeek = () => setWeekOf(dayjs().startOf('isoWeek').format('YYYY-MM-DD'))
  const isCurrentWeek = start.isSame(dayjs().startOf('isoWeek'), 'day')
  const today = dayjs().format('YYYY-MM-DD')

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">Time Sheet</h1>
          <p className="text-sm text-gray-500 mt-1">
            {formatWeekLabel(start, end)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <button
              onClick={prevWeek}
              className="px-3 py-2 hover:bg-gray-50 transition-colors border-r border-gray-200"
              title="Previous week"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
            <button
              onClick={thisWeek}
              disabled={isCurrentWeek}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-default"
            >
              Today
            </button>
            <button
              onClick={nextWeek}
              className="px-3 py-2 hover:bg-gray-50 transition-colors border-l border-gray-200"
              title="Next week"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Active Timer Banner */}
      {activeTimer && (
        <div className="mb-6 bg-cb-green/10 border border-cb-green/20 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-cb-green rounded-full animate-pulse" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                Timer running: {activeTimer.matterName || activeTimer['matter-name'] || 'Active Timer'}
              </p>
              <p className="text-xs text-gray-500">{activeTimer.description || ''}</p>
            </div>
          </div>
          <button
            onClick={() => stopTimerMutation.mutate()}
            disabled={stopTimerMutation.isPending}
            className="px-3 py-1.5 bg-cb-orange text-white rounded-lg text-xs font-medium hover:bg-cb-orange-dark transition-colors disabled:opacity-50"
          >
            {stopTimerMutation.isPending ? 'Stopping...' : 'Stop Timer'}
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-xs font-medium text-gray-500 mb-1">Total Hours</p>
          <p className="text-2xl font-semibold text-gray-900 font-display">{weekTotal.toFixed(1)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-xs font-medium text-gray-500 mb-1">Billable Hours</p>
          <p className="text-2xl font-semibold text-cb-teal font-display">{billableTotal.toFixed(1)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-xs font-medium text-gray-500 mb-1">Entries</p>
          <p className="text-2xl font-semibold text-gray-900 font-display">{entries.length}</p>
        </div>
      </div>

      {/* Weekly Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="p-12 flex justify-center">
            <LoadingSpinner message="Loading time sheet..." />
          </div>
        ) : matterRows.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-gray-500">No time entries this week</p>
            <p className="text-xs text-gray-300 mt-1">Entries will appear here as you log time</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-48">
                    Matter
                  </th>
                  {weekDates.map((d, i) => {
                    const dateKey = d.format('YYYY-MM-DD')
                    const isToday = dateKey === today
                    return (
                      <th
                        key={i}
                        className={`text-center px-2 py-3 text-xs font-semibold uppercase tracking-wider w-20 ${
                          isToday ? 'text-cb-accent-text bg-cb-accent/5' : 'text-gray-500'
                        }`}
                      >
                        <div>{DAYS[i]}</div>
                        <div className={`text-[10px] font-normal mt-0.5 ${isToday ? 'text-cb-accent-text' : 'text-gray-500'}`}>
                          {d.format('M/D')}
                        </div>
                      </th>
                    )
                  })}
                  <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {matterRows.map((matter) => {
                  const rowTotal = weekDates.reduce((sum, d) => {
                    const dateKey = d.format('YYYY-MM-DD')
                    return sum + (matter.entries[dateKey] || []).reduce((s, e) => s + e.hours, 0)
                  }, 0)

                  return (
                    <tr key={matter.name} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900 truncate" title={matter.name}>
                          {matter.name}
                        </p>
                      </td>
                      {weekDates.map((d, i) => {
                        const dateKey = d.format('YYYY-MM-DD')
                        const dayEntries = matter.entries[dateKey] || []
                        const dayHours = dayEntries.reduce((s, e) => s + e.hours, 0)
                        const isToday = dateKey === today

                        return (
                          <td
                            key={i}
                            className={`text-center px-2 py-3 ${isToday ? 'bg-cb-accent/5' : ''}`}
                            title={dayEntries.map((e) => `${e.hours}h — ${e.description || ''}`).join('\n')}
                          >
                            {dayHours > 0 ? (
                              <span className="text-sm font-medium text-gray-900">
                                {dayHours.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-200">—</span>
                            )}
                          </td>
                        )
                      })}
                      <td className="text-center px-3 py-3">
                        <span className="text-sm font-semibold text-gray-900">
                          {rowTotal.toFixed(1)}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50/80">
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-700">Daily Total</p>
                  </td>
                  {dailyTotals.map((total, i) => {
                    const isToday = weekDates[i].format('YYYY-MM-DD') === today
                    return (
                      <td key={i} className={`text-center px-2 py-3 ${isToday ? 'bg-cb-accent/5' : ''}`}>
                        <span className={`text-sm font-semibold ${total > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                          {total > 0 ? total.toFixed(1) : '—'}
                        </span>
                      </td>
                    )
                  })}
                  <td className="text-center px-3 py-3">
                    <span className="text-sm font-bold text-cb-accent-text">{weekTotal.toFixed(1)}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Detail List Below Grid */}
      {entries.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Entry Details</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {entries
              .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
              .map((entry, i) => (
                <div key={entry.id || i} className="px-5 py-3 hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-gray-900">
                          {entry.matterName || entry.name || '--'}
                        </p>
                        {entry.isNonBillable && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">
                            Non-Billable
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {entry.description || 'No description'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {entry.date ? dayjs(entry.date).format('ddd, MMM D') : '--'}
                        {entry.timekeeper ? ` — ${entry.timekeeper}` : ''}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-sm font-semibold text-gray-900">{entry.hours.toFixed(1)}h</p>
                      {entry.billableValue > 0 && (
                        <p className="text-xs text-gray-500">${entry.billableValue.toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default TimeSheet
